/**
 * AttachmentPreview
 *
 * Renders a single attachment: image thumbnail or file-type icon,
 * filename, size, progress bar when uploading, error state, remove button.
 */

import React from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Typography,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { colors } from '../theme/colors';
import type { AttachmentState } from '../hooks/useFileUpload';

interface AttachmentPreviewProps {
  attachment: AttachmentState;
  onRemove: (id: string) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') {
    return <PictureAsPdfIcon sx={{ fontSize: 28, color: colors.red[600] }} />;
  }
  if (
    mimeType.includes('spreadsheet') ||
    mimeType.includes('csv') ||
    mimeType.includes('excel')
  ) {
    return <TableChartIcon sx={{ fontSize: 28, color: colors.green[600] }} />;
  }
  return <InsertDriveFileIcon sx={{ fontSize: 28, color: colors.blue[500] }} />;
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachment,
  onRemove,
}) => {
  const { id, fileName, mimeType, sizeBytes, status, progress, errorMessage, thumbnailUrl } =
    attachment;
  const isImage = mimeType.startsWith('image/');
  const isFailed = status === 'failed';
  const isUploading = status === 'uploading';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${isFailed ? colors.red[600] : colors.gray[200]}`,
        bgcolor: isFailed ? colors.red[50] : colors.gray[50],
        maxWidth: 240,
        minWidth: 160,
      }}
    >
      {/* Thumbnail or icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: isImage && thumbnailUrl ? 'transparent' : colors.gray[100],
        }}
      >
        {isImage && thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={fileName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 4,
            }}
          />
        ) : isFailed ? (
          <ErrorOutlineIcon sx={{ fontSize: 24, color: colors.red[600] }} />
        ) : (
          <FileIcon mimeType={mimeType} />
        )}
      </Box>

      {/* File info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: '0.8rem',
            fontWeight: 500,
            color: isFailed ? colors.red[700] : colors.textPrimary,
          }}
        >
          {fileName}
        </Typography>
        {isFailed && errorMessage ? (
          <Typography
            variant="caption"
            sx={{ color: colors.red[600], fontSize: '0.7rem' }}
          >
            {errorMessage}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}
          >
            {formatSize(sizeBytes)}
          </Typography>
        )}
        {isUploading && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 0.5,
              height: 3,
              borderRadius: 2,
              bgcolor: colors.gray[200],
              '& .MuiLinearProgress-bar': {
                bgcolor: colors.blue[500],
                borderRadius: 2,
              },
            }}
          />
        )}
      </Box>

      {/* Remove button */}
      <Tooltip title="Remove">
        <IconButton
          size="small"
          onClick={() => onRemove(id)}
          sx={{
            p: 0.25,
            color: colors.gray[400],
            '&:hover': { color: colors.red[600] },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default AttachmentPreview;
